import type { AxiosInstance } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface IUseAuthenticatedImageUrlResult {
  blobUrl: string | null;
  loading: boolean;
  error: Error | null;
}

export const useAuthenticatedImageUrl = (
  src: string | undefined,
  apiInstance: AxiosInstance | null
): IUseAuthenticatedImageUrlResult => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const fetchedForSrcRef = useRef<string | null>(null);

  const isLocalOrBlobUrl =
    !src || src.startsWith('blob:') || src.startsWith('data:');
  const shouldFetch = Boolean(
    src && apiInstance && src.trim().length > 0 && !isLocalOrBlobUrl
  );

  const fetchImage = useCallback(
    async (signal: AbortSignal) => {
      if (!src || !apiInstance) return;

      setLoading(true);
      setError(null);
      try {
        const response = await apiInstance.get<Blob>(src, {
          responseType: 'blob',
          signal,
        });
        if (signal.aborted) return;
        const url = URL.createObjectURL(response.data);
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = url;
        setBlobUrl(url);
      } catch (err) {
        if (signal.aborted) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setBlobUrl(null);
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [src, apiInstance]
  );

  useEffect(() => {
    if (!shouldFetch) {
      setBlobUrl(null);
      setLoading(false);
      setError(null);
      fetchedForSrcRef.current = null;
      return undefined;
    }
    if (fetchedForSrcRef.current === src) return undefined;
    fetchedForSrcRef.current = src ?? null;
    const controller = new AbortController();
    void fetchImage(controller.signal);

    return () => {
      controller.abort();
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setBlobUrl(null);
      fetchedForSrcRef.current = null;
    };
  }, [shouldFetch, src, fetchImage]);

  return {
    blobUrl: shouldFetch ? blobUrl : null,
    loading: shouldFetch ? loading : false,
    error: shouldFetch ? error : null,
  };
};
